import { AccountBalance as BankIcon, Lock as LockIcon } from '@mui/icons-material';
import {
    Box,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { useModules } from '../contexts/ModulesContext';
import { MENU_MODE, staticMenuItems } from '../config/menuConfig';
import { presentMenuItems } from '../domain/menuVisibility';
import { type ModuleUnavailableReason, moduleUnavailableMessageKey } from '../domain/modules';
import { menuLabelKey, resolveMenuLabel } from '../domain/pagePermissions';
import { menuIconFor } from './menuIcons';

interface SidebarProps {
    collapsed: boolean;
}

interface MenuItemData {
    key: string;
    formKey: string;
    dbLabel: string;
    /** Set when the entry's module is unavailable: shown greyed out, not clickable. */
    disabledReason: ModuleUnavailableReason | null;
}

const Sidebar = ({ collapsed }: SidebarProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { menu } = useApi();
    const { user } = useAuth();
    const { modules } = useModules();
    const [menuData, setMenuData] = useState<MenuItemData[]>([]);
    const [loading, setLoading] = useState(false);

    // Read into locals so the effect depends on the role and the pages themselves rather than on
    // the user object's identity: "Schimbare utilizator" swaps the signed-in user without a
    // reload, and the menu has to follow it.
    const role = user?.role;
    const allowedPages = user?.allowedPages;

    useEffect(() => {
        const loadMenu = async () => {
            setLoading(true);

            if (MENU_MODE === 'static') {
                // Use static menu configuration
                const mapped: MenuItemData[] = presentMenuItems(
                    staticMenuItems,
                    { role, allowedPages },
                    modules,
                ).map(({ item, disabledReason }) => ({
                    key: item.path,
                    formKey: item.path.replace(/^\//, ''),
                    dbLabel: item.name,
                    disabledReason,
                }));
                setMenuData(mapped);
                setLoading(false);
                return;
            }

            // Dynamic mode: load from database
            const result = await menu.getAll();
            if (result.success) {
                const { implementedRouteNames } = await import('../routeConfig');

                const mapped: MenuItemData[] = (result.data ?? [])
                    .filter(it => {
                        const raw = it.form ?? it.Button;
                        if (!raw) return false;
                        const form = String(raw).replace(/^\//, '').toLowerCase();
                        return implementedRouteNames.has(form);
                    })
                    .map(it => {
                        const formKey = (it.form ?? it.Button ?? '').replace(/^\//, '');
                        return {
                            key: `/${it.form ?? it.Button}`,
                            formKey,
                            dbLabel: it.Name,
                            disabledReason: null,
                        };
                    });

                setMenuData(mapped);
            }
            setLoading(false);
        };
        void loadMenu();
    }, [menu, role, allowedPages, modules]);

    const getLabel = (item: MenuItemData): string => {
        const translationKey = menuLabelKey(item.key);
        // If translation returns the key itself, use database label
        return resolveMenuLabel(t(translationKey), translationKey, item.dbLabel);
    };

    const handleMenuClick = (key: string) => {
        navigate(key);
    };

    return (
        <Drawer
            variant="persistent"
            open
            sx={{
                width: collapsed ? 56 : 180,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: collapsed ? 56 : 180,
                    boxSizing: 'border-box',
                    // backgroundColor inherited from theme
                    color: 'white',
                },
            }}
        >
            <Box display="flex" alignItems="center" sx={{ p: 1.5 }}>
                <BankIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                {!collapsed && (
                    <Typography
                        variant="subtitle1"
                        sx={{
                            marginLeft: 1,
                            color: '#fff',
                            fontWeight: 'bold',
                            fontSize: '0.875rem',
                        }}
                    >
                        PawnShop
                    </Typography>
                )}
            </Box>

            {loading ? (
                <Box textAlign="center" sx={{ paddingTop: 2 }}>
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                </Box>
            ) : (
                <List dense>
                    {menuData.map(item => {
                        const button = (
                            <ListItemButton
                                data-testid={`menu-item-${item.formKey}`}
                                onClick={() => handleMenuClick(item.key)}
                                selected={location.pathname === item.key}
                                disabled={item.disabledReason !== null}
                                sx={{
                                    color: 'white',
                                    py: 0.75,
                                    minHeight: 36,
                                    '&.Mui-selected': {
                                        backgroundColor: 'primary.main',
                                    },
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                    },
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        color: 'white',
                                        minWidth: collapsed ? 0 : 32,
                                        '& svg': { fontSize: 18 },
                                    }}
                                >
                                    {menuIconFor(item.formKey)}
                                </ListItemIcon>
                                {!collapsed && (
                                    <ListItemText
                                        primary={getLabel(item)}
                                        slotProps={{ primary: { fontSize: '0.8125rem' } }}
                                    />
                                )}
                                {!collapsed && item.disabledReason !== null && (
                                    <LockIcon sx={{ fontSize: 14 }} aria-hidden />
                                )}
                            </ListItemButton>
                        );

                        return (
                            <ListItem key={item.key} disablePadding sx={{ py: 0.25 }}>
                                {item.disabledReason === null ? (
                                    button
                                ) : (
                                    // A disabled button receives no pointer events, so the
                                    // tooltip listens on a wrapper instead.
                                    <Tooltip
                                        title={t(moduleUnavailableMessageKey(item.disabledReason))}
                                        placement="right"
                                    >
                                        <span
                                            data-testid={`menu-item-${item.formKey}-locked`}
                                            style={{ display: 'block', width: '100%' }}
                                        >
                                            {button}
                                        </span>
                                    </Tooltip>
                                )}
                            </ListItem>
                        );
                    })}
                </List>
            )}
        </Drawer>
    );
};

export default Sidebar;
